import { itemTemplateApi } from "#/api/itemTemplateApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { ItemTemplate } from "#/types/itemTemplate";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import type { ColumnKey } from "#/config/tableColumnsConfig";
import { Button, Modal, Table, TextInput, Title } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconEye, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const TemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [templateToDelete, setTemplateToDelete] = useState<ItemTemplate>();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  // Header labels and visibility come from tableColumnsConfig.templates;
  // the cell and the backend sort key live here (every column sorted before).
  const templateColumns: Record<ColumnKey<"templates">, ColumnRenderer<ItemTemplate>> = {
    name: { sortKey: "name", cellClassName: "min-w-[180px]", cell: (template) => template.name ?? "--" },
    description: {
      sortKey: "description",
      cellClassName: "max-w-xs truncate min-w-[250px]",
      cell: (template) => <span title={template.description || ""}>{template.description ?? "--"}</span>,
    },
    // The backend sorts these two by the joined name, not the foreign id.
    product: { sortKey: "product", cellClassName: "min-w-[150px]", cell: (template) => template.product?.name ?? "--" },
    site: { sortKey: "site", cellClassName: "min-w-[150px]", cell: (template) => template.site?.name ?? "--" },
    location: { sortKey: "location", cellClassName: "min-w-[200px]", cell: (template) => template.location ?? "--" },
    createdAt: {
      sortKey: "createdAt",
      cellClassName: "min-w-[180px]",
      cell: (template) =>
        template.createdAt ? (
          <span>
            {formatDate(template.createdAt, client)}
            <br />
            {formatTime(template.createdAt, client)}
          </span>
        ) : (
          "--"
        ),
    },
  };
  const { visible, headers, sortableColumns, sortKeyAt, indexOf } = useTableColumns(
    "templates",
    templateColumns,
  );
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { templates = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getTemplates,
  } = useQuery({
    queryKey: ["item-templates", page, limit, sortDirection, sortBy, debouncedSearch],
    queryFn: async () => {
      const response = await itemTemplateApi.getAll({
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (!response.data) {
        throw new Error("Failed to fetch templates");
      }
      return {
        templates: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    staleTime: 1000 * 60,
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      return await itemTemplateApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("templates.deleteTemplate.success"),
      });
      closeDeleteModal();
      setTemplateToDelete(undefined);
      void getTemplates();
    },
    onError: (error) => {
      logger.error("deleteTemplate error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex flex-col gap-3 bg-white z-10">
        <div className="flex items-center justify-between">
          <Title order={2}>{t("templates.title")}</Title>
          <div className="w-fit flex items-center justify-center gap-2">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconPlus size={18} />}
                onClick={() => {
                  void navigate("/inventory/templates/new");
                }}
                color="black"
                size="sm"
                disabled={!canWrite()}
              >
                {t("templates.createTemplate")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-between">
          <TextInput
            className="flex-1 max-w-96"
            placeholder={t("templates.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 ml-auto">
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...headers, t("common.actions")]}
          loading={isPending}
          allowSorting={true}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          defaultSort={
            indexOf("createdAt") !== undefined
              ? { direction: "DESC", index: indexOf("createdAt") as number }
              : undefined
          }
          rows={templates.map((template) => {
            return (
              <Table.Tr key={template.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(template)}
                  </Table.Td>
                ))}
                <Table.Td className="min-w-[120px]">
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/inventory/templates/${template.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setTemplateToDelete(template);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title={t("templates.deleteTemplate.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <span>{t("templates.deleteTemplate.description")}</span>
            <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{templateToDelete?.name ?? "--"}</span>
              {templateToDelete?.description && (
                <span className="text-xs text-gray-600">
                  {templateToDelete.description}
                </span>
              )}
            </div>
            <span>
              {t("templates.deleteTemplate.warning")}
            </span>

            <span className="font-bold my-2">
              {t("templates.deleteTemplate.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (templateToDelete) {
                    deleteTemplateMutation.mutate(templateToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteTemplateMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("templates.deleteTemplate.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TemplatesPage;
